import * as yup from 'yup';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { TaxMode } from '../../../../api/useWithdrawMutation';
import {
  DistributionWithholdingTypeDTO,
  DistributionWithholdingUnitDTO,
  StateWitholdingDetailsDTO,
} from '../../../../api/useWithdrawalOptionsQuery';

export type AchRelationshipOption = OptionType<string>;

export interface FormValues {
  toRelationship: AchRelationshipOption | null;
  amount: string;
  federalTax: string;
  federalTaxMode: TaxMode;
  stateTax: string;
  stateTaxMode: TaxMode;
}

export interface FormContext {
  isIraAccount: boolean;
  stateWitholdingDetails: StateWitholdingDetailsDTO | null;
}

export const validation = yup.object().shape({
  toRelationship: yup
    .object()
    .nullable()
    .required('Please, select an account'),
  amount: yup
    .string()
    .nullable()
    .required('Please, enter an amount'),
  federalTax: yup
    .string()
    .nullable()
    .test(
      'federalTax-larger-than-amount',
      'Federal Tax Withholding cannot be larger than the amount',
      // eslint-disable-next-line func-names
      function(value) {
        const { amount, federalTaxMode } = this.parent;

        const { isIraAccount } = this.options.context as FormContext;

        if (!value || !amount) {
          return true;
        }

        const federalTax = parseFloat(value);
        const amountNumber = parseFloat(amount);

        return !(isIraAccount && federalTaxMode === TaxMode.Amount && federalTax > amountNumber);
      },
    )
    .test(
      'federalTax-larger-100%',
      'Federal Tax Withholding cannot be larger than 100%',
      // eslint-disable-next-line func-names
      function(value) {
        const { federalTaxMode } = this.parent;

        const { isIraAccount } = this.options.context as FormContext;

        if (!value) {
          return true;
        }

        const federalTax = parseFloat(value);

        return !(isIraAccount && federalTaxMode === TaxMode.Percentage && federalTax > 100);
      },
    ),
  stateTax: yup
    .string()
    .nullable()
    .test(
      'stateTax-larger-than-amount',
      'State Tax Withholding cannot be larger than the amount',
      // eslint-disable-next-line func-names
      function(value) {
        const { amount, stateTaxMode } = this.parent;

        const { isIraAccount } = this.options.context as FormContext;

        if (!value || !amount) {
          return true;
        }

        const stateTax = parseFloat(value);
        const amountNumber = parseFloat(amount);

        return !(isIraAccount && stateTaxMode === TaxMode.Amount && stateTax > amountNumber);
      },
    )
    .test(
      'stateTax-larger-100%',
      'State Tax Withholding cannot be larger than 100%',
      // eslint-disable-next-line func-names
      function(value) {
        const { stateTaxMode } = this.parent;

        const { isIraAccount } = this.options.context as FormContext;

        if (!value) {
          return true;
        }

        const stateTax = parseFloat(value);

        return !(isIraAccount && stateTaxMode === TaxMode.Percentage && stateTax > 100);
      },
    )
    .test(
      'withholding-rules',
      (
        value,
        {
          createError,
          path,
          parent: { federalTax: _federalTax, stateTaxMode, federalTaxMode, amount: _amount },
          options,
        },
      ) => {
        const { stateWitholdingDetails } = options.context as FormContext;

        if (!stateWitholdingDetails) {
          return true;
        }

        const {
          option: stateWithholdingType,
          minimumAmount: minimumStateWithholdingAmount,
          optOutForm: stateWithholdingOptOutForm,
          percentageRelativeToFederalTax,
          amountType,
        } = stateWitholdingDetails;

        const stateTax = parseFloat(value || '0');
        const federalTax = parseFloat(_federalTax || '0');
        const amount = parseFloat(_amount || '0');

        const absoluteStateTax =
          stateTaxMode === TaxMode.Amount ? stateTax : amount * (stateTax / 100);
        const absoluteFederalTax =
          federalTaxMode === TaxMode.Amount ? federalTax : amount * (federalTax / 100);
        const absoluteMinimumStateWithholdingAmount =
          amountType === DistributionWithholdingUnitDTO.Dollar
            ? minimumStateWithholdingAmount
            : amount * (minimumStateWithholdingAmount / 100);

        switch (stateWithholdingType) {
          case DistributionWithholdingTypeDTO.Voluntary:
            if (absoluteStateTax > 0 && absoluteStateTax < absoluteMinimumStateWithholdingAmount) {
              return createError({
                message:
                  'Your withholding amount does not meet the minimum requirement set by your state.',
                path,
              });
            }

            return true;
          case DistributionWithholdingTypeDTO.MandatoryOptOut:
            if (absoluteStateTax < absoluteMinimumStateWithholdingAmount) {
              return createError({
                message: `State form ${stateWithholdingOptOutForm} required to opt out. Please contact your advisor.`,
                path,
              });
            }

            return true;

          case DistributionWithholdingTypeDTO.Mandatory:
            if (
              (absoluteStateTax === 0 && absoluteFederalTax === 0) ||
              absoluteStateTax >= absoluteMinimumStateWithholdingAmount
            ) {
              return true;
            }

            return createError({
              message:
                'Your withholding amount does not meet the minimum requirement set by your state.',
              path,
            });

          case DistributionWithholdingTypeDTO.VoluntaryRelativeToFederal:
            if (
              absoluteStateTax > 0 &&
              absoluteFederalTax > 0 &&
              absoluteStateTax < absoluteFederalTax * (percentageRelativeToFederalTax / 100)
            ) {
              return createError({
                message:
                  'Your withholding amount does not meet the minimum requirement set by your state.',
                path,
              });
            }

            return true;

          case DistributionWithholdingTypeDTO.MandatoryRelativeToFederal:
            if (
              absoluteFederalTax > 0 &&
              absoluteStateTax < absoluteFederalTax * (percentageRelativeToFederalTax / 100)
            ) {
              return createError({
                message:
                  'Your withholding amount does not meet the minimum requirement set by your state.',
                path,
              });
            }

            return true;
          default:
            return true;
        }
      },
    ),
});

export const defaultValues = {
  toRelationship: null,
  amount: '',
  federalTax: '',
  federalTaxMode: TaxMode.Amount,
  stateTax: '',
};

export const taxOptions = [
  { value: TaxMode.Amount, label: 'USD $' },
  { value: TaxMode.Percentage, label: 'Percent %' },
];
